import React, { createContext, useContext, useState, ReactNode } from 'react';
import {
  Snackbar,
  Alert,
  AlertColor,
  Slide,
  SlideProps,
  Fade,
  Grow,
  IconButton,
  Box,
  Typography,
  Button,
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Info,
  Close,
  Login,
  PersonAdd,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';

// Custom animation keyframes
const bounceIn = keyframes`
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  70% {
    transform: scale(0.9);
    opacity: 0.9;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const slideInFromTop = keyframes`
  0% {
    transform: translateY(-100px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
`;

export type NotificationType = 'success' | 'error' | 'warning' | 'info';
export type AnimationType = 'slide' | 'fade' | 'grow' | 'bounce' | 'slideTop' | 'shake';

export interface NotificationOptions {
  type: NotificationType;
  title?: string;
  message: string;
  duration?: number;
  animation?: AnimationType;
  action?: {
    label: string;
    onClick: () => void;
  };
  persistent?: boolean;
}

interface NotificationContextType {
  showNotification: (options: NotificationOptions) => void;
  showSuccess: (message: string, options?: Partial<NotificationOptions>) => void;
  showError: (message: string, options?: Partial<NotificationOptions>) => void;
  showWarning: (message: string, options?: Partial<NotificationOptions>) => void;
  showInfo: (message: string, options?: Partial<NotificationOptions>) => void;
  showLoginError: (message: string, redirectToLogin?: () => void) => void;
  showSignupError: (message: string, redirectToLogin?: () => void) => void;
  clearNotification: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

function SlideTopTransition(props: SlideProps) {
  return <Slide {...props} direction="down" />;
}

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notification, setNotification] = useState<(NotificationOptions & { open: boolean }) | null>(null);

  const showNotification = (options: NotificationOptions) => {
    setNotification({
      ...options,
      open: true,
      duration: options.duration ?? 6000,
      animation: options.animation ?? 'slide',
    });
  };

  const showSuccess = (message: string, options?: Partial<NotificationOptions>) => {
    showNotification({
      type: 'success',
      message,
      animation: 'bounce',
      ...options,
    });
  };

  const showError = (message: string, options?: Partial<NotificationOptions>) => {
    showNotification({
      type: 'error',
      message,
      animation: 'shake',
      duration: 8000,
      ...options,
    });
  };

  const showWarning = (message: string, options?: Partial<NotificationOptions>) => {
    showNotification({
      type: 'warning',
      message,
      animation: 'slideTop',
      ...options,
    });
  };

  const showInfo = (message: string, options?: Partial<NotificationOptions>) => {
    showNotification({
      type: 'info',
      message,
      animation: 'fade',
      ...options,
    });
  };

  const showLoginError = (message: string, redirectToLogin?: () => void) => {
    showNotification({
      type: 'error',
      title: 'Login Failed',
      message,
      animation: 'shake',
      duration: 10000,
      action: redirectToLogin ? {
        label: 'Go to Login',
        onClick: redirectToLogin,
      } : undefined,
    });
  };

  const showSignupError = (message: string, redirectToLogin?: () => void) => {
    showNotification({
      type: 'warning',
      title: 'Account Already Exists',
      message,
      animation: 'slideTop',
      duration: 12000,
      action: redirectToLogin ? {
        label: 'Login Instead',
        onClick: redirectToLogin,
      } : undefined,
    });
  };

  const clearNotification = () => {
    setNotification(null);
  };

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway' && notification?.persistent) {
      return;
    }
    setNotification(null);
  };

  const getIcon = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return <CheckCircle />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <Warning />;
      case 'info':
        return <Info />;
      default:
        return <Info />;
    }
  };

  const getAnimationStyle = (animation: AnimationType) => {
    switch (animation) {
      case 'bounce':
        return {
          animation: `${bounceIn} 0.6s ease-out`,
        };
      case 'slideTop':
        return {
          animation: `${slideInFromTop} 0.5s ease-out`,
        };
      case 'shake':
        return {
          animation: `${shake} 0.6s ease-in-out`,
        };
      default:
        return {};
    }
  };

  const getTransitionComponent = (animation: AnimationType) => {
    switch (animation) {
      case 'slide':
        return SlideTransition;
      case 'slideTop':
        return SlideTopTransition;
      case 'fade':
        return Fade;
      case 'grow':
        return Grow;
      default:
        return SlideTransition;
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        showLoginError,
        showSignupError,
        clearNotification,
      }}
    >
      {children}
      
      {notification && (
        <Snackbar
          open={notification.open}
          autoHideDuration={notification.persistent ? null : notification.duration}
          onClose={handleClose}
          TransitionComponent={getTransitionComponent(notification.animation!)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{
            '& .MuiSnackbarContent-root': {
              minWidth: '400px',
              maxWidth: '600px',
            },
          }}
        >
          <Alert
            onClose={handleClose}
            severity={notification.type}
            icon={getIcon(notification.type)}
            variant="filled"
            sx={{
              width: '100%',
              borderRadius: 2,
              boxShadow: 3,
              ...getAnimationStyle(notification.animation!),
              '& .MuiAlert-icon': {
                fontSize: '1.5rem',
              },
              '& .MuiAlert-message': {
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              },
            }}
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {notification.action && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={notification.action.onClick}
                    startIcon={notification.title?.includes('Login') ? <Login /> : <PersonAdd />}
                    sx={{
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    {notification.action.label}
                  </Button>
                )}
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={handleClose}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            }
          >
            <Box>
              {notification.title && (
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {notification.title}
                </Typography>
              )}
              <Typography variant="body2">
                {notification.message}
              </Typography>
            </Box>
          </Alert>
        </Snackbar>
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};